import React, { useMemo, useState } from 'react';
import { BarChart3, Users, MousePointerClick, Calendar as CalendarIcon, TrendingUp, ArrowUpRight, MessageCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getPageViewsInRange, getPageViews, PageViewEvent } from '../utils/analytics';
type Preset = 'hoy' | '7dias' | '30dias' | 'todo' | 'custom';
export function Control() {
  const [preset, setPreset] = useState<Preset>('7dias');
  // Default to last 7 days
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const handlePresetChange = (newPreset: Preset) => {
    setPreset(newPreset);
    const end = new Date();
    const start = new Date();
    switch (newPreset) {
      case 'hoy':
        // start is already today
        break;
      case '7dias':
        start.setDate(start.getDate() - 6);
        break;
      case '30dias':
        start.setDate(start.getDate() - 29);
        break;
      case 'todo':
        // Just set to a very old date
        start.setFullYear(2020);
        break;
      case 'custom':
        return;
      // Don't change dates, just the preset
    }
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };
  // Fetch and process data
  const {
    kpis,
    dailyData
  } = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // If 'todo', just get all views, otherwise filter
    const events = preset === 'todo' ? getPageViews() : getPageViewsInRange(start, end);
    let registroCount = 0;
    let graciasCount = 0;
    let whatsappCount = 0;
    // Group by date for the chart and table
    const groupedByDate: Record<string, {
      date: string;
      registro: number;
      gracias: number;
      whatsapp: number;
    }> = {};
    events.forEach(event => {
      const dateStr = event.timestamp.split('T')[0];
      if (!groupedByDate[dateStr]) {
        groupedByDate[dateStr] = {
          date: dateStr,
          registro: 0,
          gracias: 0,
          whatsapp: 0
        };
      }
      if (event.page === '/registro') {
        registroCount++;
        groupedByDate[dateStr].registro++;
      } else if (event.page === '/gracias') {
        graciasCount++;
        groupedByDate[dateStr].gracias++;
      } else if (event.page === 'whatsapp_click') {
        whatsappCount++;
        groupedByDate[dateStr].whatsapp++;
      }
    });
    // Convert grouped object to array and sort by date (newest first for table, oldest first for chart)
    const dailyArray = Object.values(groupedByDate).sort((a, b) => a.date.localeCompare(b.date));
    const ctr = registroCount > 0 ? (graciasCount / registroCount * 100).toFixed(1) : '0.0';
    const whatsappCtr = graciasCount > 0 ? (whatsappCount / graciasCount * 100).toFixed(1) : '0.0';
    return {
      kpis: {
        registro: registroCount,
        gracias: graciasCount,
        whatsapp: whatsappCount,
        ctr: ctr,
        whatsappCtr: whatsappCtr
      },
      dailyData: dailyArray
    };
  }, [startDate, endDate, preset]);
  // Reverse for table (newest first)
  const tableData = [...dailyData].reverse();
  return <div className="min-h-screen bg-gray-50 font-sans text-dark flex flex-col md:flex-row" data-id="element-23">
      {/* SIDEBAR */}
      <aside className="w-full md:w-64 bg-dark text-white p-6 flex-shrink-0" data-id="element-24">
        <div className="flex items-center gap-3 mb-10" data-id="element-25">
          <div className="w-10 h-10 bg-gold-metallic rounded-lg flex items-center justify-center" data-id="element-26">
            <BarChart3 className="w-6 h-6 text-dark" data-id="element-27" />
          </div>
          <div data-id="element-28">
            <h1 className="font-bold text-xl leading-tight" data-id="element-29">
              Panel de Control
            </h1>
            <p className="text-xs text-gray-400" data-id="element-30">Webinar Analytics</p>
          </div>
        </div>

        <nav className="space-y-2" data-id="element-31">
          <a href="#" className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-lg text-gold-light font-medium transition-colors" data-id="element-32">
            <TrendingUp className="w-5 h-5" data-id="element-33" />
            Métricas
          </a>
          <a href="/registro" target="_blank" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-lg text-gray-300 transition-colors" data-id="element-34">
            <ArrowUpRight className="w-5 h-5" data-id="element-35" />
            Ver Landing
          </a>
        </nav>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto" data-id="element-36">
        <div className="max-w-6xl mx-auto space-y-8" data-id="element-37">
          {/* HEADER & FILTERS */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-38">
            <div data-id="element-39">
              <h2 className="text-2xl font-bold text-dark mb-1" data-id="element-40">
                Rendimiento del Embudo
              </h2>
              <p className="text-gray-500 text-sm" data-id="element-41">
                Visualiza el tráfico y conversión de tus páginas.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-42">
              <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-43">
                <button onClick={() => handlePresetChange('hoy')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === 'hoy' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-44">
                  Hoy
                </button>
                <button onClick={() => handlePresetChange('7dias')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === '7dias' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-45">
                  7 Días
                </button>
                <button onClick={() => handlePresetChange('30dias')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === '30dias' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-46">
                  30 Días
                </button>
                <button onClick={() => handlePresetChange('todo')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === 'todo' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-47">
                  Todo
                </button>
              </div>

              <div className="flex items-center gap-2" data-id="element-48">
                <div className="relative" data-id="element-49">
                  <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-50" />
                  <input type="date" value={startDate} onChange={e => {
                  setStartDate(e.target.value);
                  setPreset('custom');
                }} className="pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gold-metallic focus:border-gold-metallic outline-none" data-id="element-51" />
                </div>
                <span className="text-gray-400" data-id="element-52">-</span>
                <div className="relative" data-id="element-53">
                  <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-54" />
                  <input type="date" value={endDate} onChange={e => {
                  setEndDate(e.target.value);
                  setPreset('custom');
                }} className="pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gold-metallic focus:border-gold-metallic outline-none" data-id="element-55" />
                </div>
              </div>
            </div>
          </div>

          {/* KPI CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-56">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-57">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-58">
                <Users className="w-6 h-6" data-id="element-59" />
              </div>
              <div data-id="element-60">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-61">
                  Visitas Registro
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-62">{kpis.registro}</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-63">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-64">
                <MousePointerClick className="w-6 h-6" data-id="element-65" />
              </div>
              <div data-id="element-66">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-67">
                  Visitas Gracias
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-68">{kpis.gracias}</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 relative overflow-hidden" data-id="element-69">
              <div className="absolute right-0 top-0 w-24 h-24 bg-gold-metallic/10 rounded-bl-full -mr-4 -mt-4" data-id="element-70"></div>
              <div className="w-12 h-12 bg-gold-light text-gold-dark rounded-xl flex items-center justify-center flex-shrink-0 relative z-10" data-id="element-71">
                <TrendingUp className="w-6 h-6" data-id="element-72" />
              </div>
              <div className="relative z-10" data-id="element-73">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-74">
                  CTR Registro→Gracias
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-75">{kpis.ctr}%</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-76">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-77">
                <MessageCircle className="w-6 h-6" data-id="element-78" />
              </div>
              <div data-id="element-79">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-80">
                  Clics WhatsApp
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-81">{kpis.whatsapp}</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 relative overflow-hidden" data-id="element-82">
              <div className="absolute right-0 top-0 w-24 h-24 bg-emerald-500/10 rounded-bl-full -mr-4 -mt-4" data-id="element-83"></div>
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 relative z-10" data-id="element-84">
                <TrendingUp className="w-6 h-6" data-id="element-85" />
              </div>
              <div className="relative z-10" data-id="element-86">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-87">
                  CTR Gracias→WhatsApp
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-88">
                  {kpis.whatsappCtr}%
                </p>
              </div>
            </div>
          </div>

          {/* CHART */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-89">
            <h3 className="text-lg font-bold text-dark mb-6" data-id="element-90">
              Tráfico a lo largo del tiempo
            </h3>
            <div className="h-[300px] w-full" data-id="element-91">
              {dailyData.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-92">
                  <LineChart data={dailyData} margin={{
                top: 5,
                right: 20,
                bottom: 5,
                left: 0
              }} data-id="element-93">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-94" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#6b7280',
                  fontSize: 12
                }} dy={10} data-id="element-95" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#6b7280',
                  fontSize: 12
                }} data-id="element-96" />
                    <Tooltip contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-97" />
                    <Legend iconType="circle" wrapperStyle={{
                  paddingTop: '20px'
                }} data-id="element-98" />
                    <Line type="monotone" name="Registro" dataKey="registro" stroke="#1f2937" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-99" />
                    <Line type="monotone" name="Gracias" dataKey="gracias" stroke="#D4AF37" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-100" />
                    <Line type="monotone" name="WhatsApp" dataKey="whatsapp" stroke="#25D366" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-101" />
                  </LineChart>
                </ResponsiveContainer> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-102">
                  No hay datos en este rango de fechas
                </div>}
            </div>
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-103">
            <div className="p-6 border-b border-gray-100" data-id="element-104">
              <h3 className="text-lg font-bold text-dark" data-id="element-105">Desglose Diario</h3>
            </div>
            <div className="overflow-x-auto" data-id="element-106">
              <table className="w-full text-left border-collapse" data-id="element-107">
                <thead data-id="element-108">
                  <tr className="bg-gray-50 text-gray-500 text-sm" data-id="element-109">
                    <th className="px-6 py-4 font-medium" data-id="element-110">Fecha</th>
                    <th className="px-6 py-4 font-medium" data-id="element-111">Registro</th>
                    <th className="px-6 py-4 font-medium" data-id="element-112">Gracias</th>
                    <th className="px-6 py-4 font-medium" data-id="element-113">CTR %</th>
                    <th className="px-6 py-4 font-medium" data-id="element-114">WhatsApp</th>
                    <th className="px-6 py-4 font-medium" data-id="element-115">CTR WA %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-116">
                  {tableData.length > 0 ? tableData.map(row => {
                  const rowCtr = row.registro > 0 ? (row.gracias / row.registro * 100).toFixed(1) : '0.0';
                  const rowWaCtr = row.gracias > 0 ? (row.whatsapp / row.gracias * 100).toFixed(1) : '0.0';
                  return <tr key={row.date} className="hover:bg-gray-50/50 transition-colors" data-id="element-117">
                          <td className="px-6 py-4 text-sm font-medium text-dark" data-id="element-118">
                            {row.date}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-119">
                            {row.registro}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-120">
                            {row.gracias}
                          </td>
                          <td className="px-6 py-4 text-sm" data-id="element-121">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${Number(rowCtr) > 20 ? 'bg-green-100 text-green-700' : Number(rowCtr) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-122">
                              {rowCtr}%
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-123">
                            {row.whatsapp}
                          </td>
                          <td className="px-6 py-4 text-sm" data-id="element-124">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${Number(rowWaCtr) > 20 ? 'bg-emerald-100 text-emerald-700' : Number(rowWaCtr) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-125">
                              {rowWaCtr}%
                            </span>
                          </td>
                        </tr>;
                }) : <tr data-id="element-126">
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-400 text-sm" data-id="element-127">
                        No hay datos para mostrar
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>;
}